import React from 'react';
import type {
  DepartmentOption,
  ExicCardOption,
  OrganizationBaseOption,
  ReservationInfo
} from '../../../api/reservationInfo';
import type { ShinkansenFormValues } from '../../../types/reservationInfoForm';
import {
  isShinkansenPostalTicketing,
  PREFERRED_TICKETING_METHOD_LABELS,
  PREFERRED_TICKETING_METHODS,
  SHINKANSEN_ADDRESS_TYPES,
  SHINKANSEN_ADDRESS_TYPE_LABELS
} from '../../../types/reservationInfoForm';

type ShinkansenErrors = Partial<Record<keyof ShinkansenFormValues, string>>;

type Props = {
  shinkansen: ShinkansenFormValues;
  shinkansenErrors: ShinkansenErrors;
  settings: NonNullable<ReservationInfo['shinkansenInfo']>['settings'];
  departments: DepartmentOption[];
  organizationBases: OrganizationBaseOption[];
  exicCards: ExicCardOption[];
  onSelectChange: (field: keyof ShinkansenFormValues) => (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onTextChange: (field: keyof ShinkansenFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddressTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onDepartmentChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onOrganizationBaseChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled: boolean;
};

const ReservationInfoShinkansenSection = ({
  shinkansen,
  shinkansenErrors,
  settings,
  departments,
  organizationBases,
  exicCards,
  onSelectChange,
  onTextChange,
  onAddressTypeChange,
  onDepartmentChange,
  onOrganizationBaseChange,
  disabled
}: Props) => {
  const styles = getStyles();
  return (
    <div style={styles.section}>
      <h4 style={styles.sectionTitle}>新幹線・特急</h4>
      <div style={styles.formGroup}>
        <label htmlFor="shinkansen-ticket-type" style={styles.label}>
          <span>券種</span>
          <select
            id="shinkansen-ticket-type"
            style={styles.select}
            value={shinkansen.shinkansenTicketType}
            onChange={onSelectChange('shinkansenTicketType')}
            disabled={disabled}
          >
            <option value="normal">通常きっぷ</option>
            {settings.showEx && <option value="ex">EX-IC</option>}
            {settings.showBusinessEkinet && <option value="business_ekinet">ビジネスえきねっと</option>}
            {settings.useKyuusyuuTicket && <option value="kyuusyuu">JR九州きっぷ</option>}
            {settings.useBulkTicket && <option value="bulk_ticket">回数券</option>}
          </select>
        </label>
      </div>
      <div style={styles.formGroup}>
        <label htmlFor="shinkansen-seat-type" style={styles.label}>
          <span>座席</span>
          <select
            id="shinkansen-seat-type"
            style={styles.select}
            value={shinkansen.shinkansenSeatType}
            onChange={onSelectChange('shinkansenSeatType')}
            disabled={disabled}
          >
            <option value="free">自由席</option>
            <option value="reserve">指定席</option>
            <option value="green">グリーン席</option>
          </select>
        </label>
      </div>
      {(shinkansen.shinkansenSeatType === 'reserve' ||
        shinkansen.shinkansenSeatType === 'green' ||
        shinkansen.shinkansenTicketType === 'ex') && (
        <div style={styles.formGroup}>
          <label htmlFor="shinkansen-seat" style={styles.label}>
            <span>座席の希望</span>
            <select
              id="shinkansen-seat"
              style={styles.select}
              value={shinkansen.shinkansenSeat}
              onChange={onSelectChange('shinkansenSeat')}
              disabled={disabled}
            >
              <option value="指定なし">指定なし</option>
              <option value="窓側">窓側</option>
              <option value="通路側">通路側</option>
              <option value="その他">その他</option>
            </select>
          </label>
          {shinkansen.shinkansenSeat === 'その他' && (
            <input
              type="text"
              style={styles.input}
              placeholder="(任意)"
              value={shinkansen.shinkansenSeatDetail}
              onChange={onTextChange('shinkansenSeatDetail')}
              disabled={disabled}
            />
          )}
        </div>
      )}
      {shinkansen.shinkansenTicketType === 'ex' && settings.exicUsageMode === 'user_based' && (
        <>
          <div style={styles.formGroup}>
            <label htmlFor="shinkansen-exic-number" style={styles.label}>
              <span>EXIC会員ID</span>
              <input
                id="shinkansen-exic-number"
                type="text"
                style={styles.input}
                value={shinkansen.exicNumber}
                onChange={onTextChange('exicNumber')}
                disabled={disabled}
              />
            </label>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="shinkansen-exic-password" style={styles.label}>
              <span>EXICパスワード</span>
              <input
                id="shinkansen-exic-password"
                type="password"
                style={styles.input}
                value={shinkansen.exicPassword}
                onChange={onTextChange('exicPassword')}
                disabled={disabled}
              />
            </label>
          </div>
        </>
      )}
      {shinkansen.shinkansenTicketType === 'ex' && settings.exicUsageMode === 'card_based' && (
        <div style={styles.formGroup}>
          <label htmlFor="shinkansen-exic-card" style={styles.label}>
            <span>EXIC会員ID</span>
            <span style={styles.required}>*</span>
            <select
              id="shinkansen-exic-card"
              style={{
                ...styles.select,
                ...(shinkansenErrors.exicCardId ? styles.inputError : {})
              }}
              value={shinkansen.exicCardId || ''}
              onChange={onSelectChange('exicCardId')}
              disabled={disabled || settings.allowUserExicInput === false}
            >
              <option value="">選択してください</option>
              {exicCards.map(card => (
                <option key={card.id} value={card.id}>
                  {card.exicNumber}
                </option>
              ))}
            </select>
          </label>
          {shinkansenErrors.exicCardId && <span style={styles.errorText}>{shinkansenErrors.exicCardId}</span>}
        </div>
      )}
      {settings.useQr && settings.usePostal && (
        <div style={styles.formGroup}>
          <label htmlFor="shinkansen-preferred-ticketing-method" style={styles.label}>
            <span>発券方法</span>
            <select
              id="shinkansen-preferred-ticketing-method"
              style={styles.select}
              value={shinkansen.preferredTicketingMethod}
              onChange={onSelectChange('preferredTicketingMethod')}
              disabled={disabled}
            >
              <option value={PREFERRED_TICKETING_METHODS.qr}>
                {PREFERRED_TICKETING_METHOD_LABELS[PREFERRED_TICKETING_METHODS.qr]}
              </option>
              <option value={PREFERRED_TICKETING_METHODS.postal}>
                {PREFERRED_TICKETING_METHOD_LABELS[PREFERRED_TICKETING_METHODS.postal]}
              </option>
            </select>
          </label>
        </div>
      )}
      {isShinkansenPostalTicketing(shinkansen, settings) &&
        shinkansen.shinkansenTicketType !== 'ex' &&
        shinkansen.shinkansenTicketType !== 'business_ekinet' && (
          <>
            <div style={styles.formGroup}>
              <label htmlFor="shinkansen-address-type" style={styles.label}>
                <span>切符送り先の選択</span>
                <select
                  id="shinkansen-address-type"
                  style={styles.select}
                  value={shinkansen.shinkansenAddressType}
                  onChange={onAddressTypeChange}
                  disabled={disabled}
                >
                  {settings.enabledCustomizeAddress && (
                    <option value={SHINKANSEN_ADDRESS_TYPES.text}>
                      {SHINKANSEN_ADDRESS_TYPE_LABELS[SHINKANSEN_ADDRESS_TYPES.text]}
                    </option>
                  )}
                  {settings.hasDepartments && (
                    <option value={SHINKANSEN_ADDRESS_TYPES.select}>
                      {SHINKANSEN_ADDRESS_TYPE_LABELS[SHINKANSEN_ADDRESS_TYPES.select]}
                    </option>
                  )}
                  {settings.hasOrganizationBases && (
                    <option value={SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect}>
                      {SHINKANSEN_ADDRESS_TYPE_LABELS[SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect]}
                    </option>
                  )}
                </select>
              </label>
            </div>
            {shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.text &&
              settings.enabledCustomizeAddress && (
                <>
                  <div style={styles.formGroup}>
                    <label htmlFor="shinkansen-postcode" style={styles.label}>
                      郵便番号
                      {shinkansenErrors.postcode && <span style={styles.required}>*</span>}
                    </label>
                    <input
                      id="shinkansen-postcode"
                      type="text"
                      style={{
                        ...styles.input,
                        ...(shinkansenErrors.postcode ? styles.inputError : {})
                      }}
                      value={shinkansen.postcode}
                      onChange={onTextChange('postcode')}
                      disabled={disabled}
                    />
                    {shinkansenErrors.postcode && (
                      <span style={styles.errorText}>{shinkansenErrors.postcode}</span>
                    )}
                  </div>
                  <div style={styles.formGroup}>
                    <label htmlFor="shinkansen-address" style={styles.label}>
                      切符送り先住所
                      {shinkansenErrors.shinkansenAddress && <span style={styles.required}>*</span>}
                    </label>
                    <input
                      id="shinkansen-address"
                      type="text"
                      style={{
                        ...styles.input,
                        ...(shinkansenErrors.shinkansenAddress ? styles.inputError : {})
                      }}
                      value={shinkansen.shinkansenAddress}
                      onChange={onTextChange('shinkansenAddress')}
                      disabled={disabled}
                    />
                    {shinkansenErrors.shinkansenAddress && (
                      <span style={styles.errorText}>{shinkansenErrors.shinkansenAddress}</span>
                    )}
                  </div>
                  <div style={styles.formGroup}>
                    <label htmlFor="shinkansen-addressee" style={styles.label}>
                      切符送り先宛名
                      {shinkansenErrors.shinkansenAddressee && <span style={styles.required}>*</span>}
                    </label>
                    <input
                      id="shinkansen-addressee"
                      type="text"
                      style={{
                        ...styles.input,
                        ...(shinkansenErrors.shinkansenAddressee ? styles.inputError : {})
                      }}
                      value={shinkansen.shinkansenAddressee}
                      onChange={onTextChange('shinkansenAddressee')}
                      disabled={disabled}
                    />
                    {shinkansenErrors.shinkansenAddressee && (
                      <span style={styles.errorText}>{shinkansenErrors.shinkansenAddressee}</span>
                    )}
                  </div>
                </>
              )}
            {shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.select && (
              <div style={styles.formGroup}>
                <label htmlFor="shinkansen-department" style={styles.label}>
                  部署
                  {shinkansenErrors.departmentId && <span style={styles.required}>*</span>}
                </label>
                <select
                  id="shinkansen-department"
                  style={{
                    ...styles.select,
                    ...(shinkansenErrors.departmentId ? styles.inputError : {})
                  }}
                  value={shinkansen.departmentId || ''}
                  onChange={onDepartmentChange}
                  disabled={disabled}
                >
                  <option value="">選択してください</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {shinkansenErrors.departmentId && (
                  <span style={styles.errorText}>{shinkansenErrors.departmentId}</span>
                )}
              </div>
            )}
            {shinkansen.shinkansenAddressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect && (
              <div style={styles.formGroup}>
                <label htmlFor="shinkansen-base" style={styles.label}>
                  <span>拠点</span>
                  <select
                    id="shinkansen-base"
                    style={styles.select}
                    value={shinkansen.organizationBaseId || ''}
                    onChange={onOrganizationBaseChange}
                    disabled={disabled}
                  >
                    <option value="">選択してください</option>
                    {organizationBases.map(base => (
                      <option key={base.id} value={base.id}>
                        {base.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </>
        )}
    </div>
  );
};

const getStyles = (): Record<string, React.CSSProperties> => ({
  section: {
    marginTop: '16px',
    paddingTop: '8px',
    borderTop: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 'bold',
    marginBottom: '12px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '6px',
    color: '#333'
  },
  required: {
    color: '#e74c3c',
    marginLeft: '4px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  inputError: {
    borderColor: '#e74c3c'
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  }
});

export default ReservationInfoShinkansenSection;
